import { useQueryClient } from '@tanstack/react-query';
import { RoleApiModel } from '../../../../types/models/role.model';
import { CreateRoleApiRequest } from '../../../../types/requests/roles/create-role.request';
import { DeleteRoleApiRequest } from '../../../../types/requests/roles/delete-role.request';
import { GetRoleApiRequest } from '../../../../types/requests/roles/get-role.request';
import { SearchRolesApiRequest } from '../../../../types/requests/roles/search-roles.request';
import { UpdateRoleApiRequest } from '../../../../types/requests/roles/update-role.request';
import { createRole, deleteRole, getRole, searchRoles, updateRole } from '../../api/roles.api';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';

const baseQueryKey = 'role';
const searchQueryKey = 'search';

export const useCreateRole = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreateRoleApiRequest) => (await createRole(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, { ...result, role: data }] as const;
};

export const useUpdateRole = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdateRoleApiRequest) => (await updateRole(params)).data,
    onSuccess: (role) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === searchQueryKey || (queryKey[1] as GetRoleApiRequest)?.roleId === role.id),
      }),
  });
  return [request, { ...result, role: data }] as const;
};

export const useGetRole = <T extends RoleApiModel>(params: GetRoleApiRequest) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getRole<T>(params)).data,
    enabled: Boolean(params.roleId && !Number.isNaN(params.roleId)),
  });
  return { ...result, role: data } as const;
};

export const useSearchRoles = (params: SearchRolesApiRequest) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) => (await searchRoles({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
  });
  return { ...result, response: data } as const;
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeleteRoleApiRequest) => (await deleteRole(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};
